// Custom hook for Chart of Accounts tree state management

import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAccountTypes } from '@/app/services/accountTypeService';
import { listAccountGroups } from '@/app/services/accountGroupService';
import { listAccounts } from '@/app/services/accountService';
import { useAuthStore } from '@/app/store/authStore';
import { TreeNode } from './treeTypes';
import {
  buildTreeNodes,
  getVisibleNodes,
  getAncestorIds,
  filterNodesBySearch,
} from './treeUtils';

interface UseChartOfAccountsTreeOptions {
  initialExpandedIds?: Set<string>;
}

interface UseChartOfAccountsTreeReturn {
  // Data
  allNodes: TreeNode[];
  visibleNodes: TreeNode[];
  isLoading: boolean;
  error: Error | null;

  // Expansion state
  expandedIds: Set<string>;
  toggleExpand: (nodeId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  expandToNode: (nodeId: string) => void;

  // Search/Filter
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showInactive: boolean;
  setShowInactive: (show: boolean) => void;

  // Selection
  selectedNodeId: string | null;
  setSelectedNodeId: (id: string | null) => void;

  // Keyboard navigation
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  moveFocusUp: () => void;
  moveFocusDown: () => void;

  // Refresh
  refetch: () => void;
}

export function useChartOfAccountsTree(
  options: UseChartOfAccountsTreeOptions = {}
): UseChartOfAccountsTreeReturn {
  const { userOrgId } = useAuthStore();
  const orgId = userOrgId ?? undefined;

  // State
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    options.initialExpandedIds || new Set()
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Fetch account types
  const {
    data: accountTypesData,
    isLoading: isLoadingTypes,
    error: typesError,
  } = useQuery({
    queryKey: ['accountTypes'],
    queryFn: () => listAccountTypes({ limit: 100 }),
  });

  // Fetch all account groups
  // Note: Backend handles org scoping automatically based on auth token
  const {
    data: accountGroupsData,
    isLoading: isLoadingGroups,
    error: groupsError,
    refetch: refetchGroups,
  } = useQuery({
    queryKey: ['accountGroups', orgId, 'all'],
    queryFn: () =>
      listAccountGroups({
        limit: 500,
        sort_by: 'name',
        sort_dir: 'asc',
        filters: '[]', // Prevent backend warning
      }),
    enabled: !!orgId,
  });

  // Fetch all accounts
  // Note: Backend handles org scoping automatically based on auth token
  const {
    data: accountsData,
    isLoading: isLoadingAccounts,
    error: accountsError,
    refetch: refetchAccounts,
  } = useQuery({
    queryKey: ['accounts', orgId, 'all'],
    queryFn: () =>
      listAccounts({
        limit: 500,
        sort_by: 'name',
        sort_dir: 'asc',
        filters: '[]', // Prevent backend warning
      }),
    enabled: !!orgId,
  });

  const isLoading = isLoadingTypes || isLoadingGroups || isLoadingAccounts;
  const error = typesError || groupsError || accountsError;

  // Build tree nodes
  const allNodes = useMemo(() => {
    if (!accountTypesData?.items || !accountGroupsData?.items || !accountsData?.items) {
      return [];
    }

    return buildTreeNodes(
      accountTypesData.items,
      accountGroupsData.items,
      accountsData.items
    );
  }, [accountTypesData?.items, accountGroupsData?.items, accountsData?.items]);

  // Filter nodes by search and inactive status
  const filteredNodes = useMemo(() => {
    return filterNodesBySearch(allNodes, searchQuery, showInactive);
  }, [allNodes, searchQuery, showInactive]);

  // Get visible nodes based on expansion state
  const visibleNodes = useMemo(() => {
    return getVisibleNodes(filteredNodes, expandedIds);
  }, [filteredNodes, expandedIds]);

  // Toggle expand/collapse for a node
  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Expand all nodes
  const expandAll = useCallback(() => {
    const allExpandableIds = allNodes
      .filter((node) => node.hasChildren)
      .map((node) => node.id);
    setExpandedIds(new Set(allExpandableIds));
  }, [allNodes]);

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  // Expand to show a specific node
  const expandToNode = useCallback(
    (nodeId: string) => {
      const ancestors = getAncestorIds(nodeId, allNodes);
      setExpandedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((id) => next.add(id));
        return next;
      });
    },
    [allNodes]
  );

  // Keyboard navigation
  const moveFocusUp = useCallback(() => {
    setFocusedIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const moveFocusDown = useCallback(() => {
    setFocusedIndex((prev) => Math.min(visibleNodes.length - 1, prev + 1));
  }, [visibleNodes.length]);

  // Refetch all data
  const refetch = useCallback(() => {
    refetchGroups();
    refetchAccounts();
  }, [refetchGroups, refetchAccounts]);

  return {
    allNodes,
    visibleNodes,
    isLoading,
    error: error as Error | null,

    expandedIds,
    toggleExpand,
    expandAll,
    collapseAll,
    expandToNode,

    searchQuery,
    setSearchQuery,
    showInactive,
    setShowInactive,

    selectedNodeId,
    setSelectedNodeId,

    focusedIndex,
    setFocusedIndex,
    moveFocusUp,
    moveFocusDown,

    refetch,
  };
}
